import classnames from 'classnames';
import React, {
    CSSProperties,
    Children,
    ReactNode,
    RefObject,
    forwardRef,
    useImperativeHandle,
    useRef,
    PropsWithChildren,
    useMemo,
    useCallback,
} from 'react';

import { useScroller } from '../Scroller/useScroller';

import styles from './Carousel.module.css';
import { CarouselNav } from './CarouselNav';
import { withVisibilityFade } from './withVisibilityFade';

const getNumericStyleValue = (computedStyle: CSSStyleDeclaration, property: string): number =>
    parseInt(computedStyle.getPropertyValue(property) || '0', 10);

/**
 * Calculates the scroll distance needed to navigate the carousel based on visible slides.
 *
 * @param carousel - The HTML element representing the carousel container
 * @param slides - Array of HTML elements representing individual slides
 * @returns The scroll distance in pixels needed to move to show the next set of fully visible slides.
 *          Returns 0 if there are no slides.
 *
 * The calculation takes into account:
 * - Slide width
 * - Container width and padding
 * - Grid column gap between slides
 * - Number of fully visible slides (rounded to one decimal place)
 */
const calculateScrollDistance = (carousel: HTMLElement): number => {
    const firstSlide = carousel.getElementsByClassName(styles.carouselSlide)[0];

    if (!(firstSlide instanceof HTMLElement)) return 0;

    const computedStyle = window.getComputedStyle(carousel);
    const paddingLeft = getNumericStyleValue(computedStyle, 'padding-left');
    const paddingRight = getNumericStyleValue(computedStyle, 'padding-right');

    const containerWidth = carousel.offsetWidth;
    const availableWidth = containerWidth - paddingLeft - paddingRight;

    return availableWidth;
};

export interface CarouselHandle {
    scrollerRef: RefObject<HTMLElement>;
    scrollToIndex: (index: number, options?: ScrollIntoViewOptions) => void;
    scrollToPrevious: VoidFunction;
    scrollToNext: VoidFunction;
    refresh: VoidFunction;
}

interface CarouselRenderProps {
    carouselRef: RefObject<HTMLDivElement>;
    scrollerRef: RefObject<HTMLElement>;
    scrollToPrevious: VoidFunction;
    scrollToNext: VoidFunction;
}

interface DefaultSlideWrapperProps {
    className?: string;
}

const SlideWrapper = forwardRef<HTMLLIElement, PropsWithChildren<DefaultSlideWrapperProps>>(
    ({ className, children }, ref) => (
        <li className={classnames(styles.carouselSlide, className)} ref={ref}>
            {children}
        </li>
    )
);

export const WithVisibilityFadeSlideWrapper = withVisibilityFade(SlideWrapper);

interface CarouselProps {
    children: ReactNode | ((api: Partial<CarouselRenderProps>) => ReactNode);
    id?: string;
    className?: string;
    slideClassName?: string;
    scrollerContentClassName?: string;
    navButtonsClassName?: string;
    style?: CSSProperties;
    fadeOnVisibility?: boolean;
    initialIndex?: number;
}

export const Carousel = forwardRef<CarouselHandle, CarouselProps>(
    (
        {
            children,
            id,
            className,
            slideClassName,
            scrollerContentClassName,
            navButtonsClassName,
            style,
            fadeOnVisibility = true,
            initialIndex,
        },
        ref
    ) => {
        const carouselRef = useRef<HTMLDivElement>(null);
        const { scrollerRef, scroller } = useScroller<HTMLDivElement>({
            itemSelector: `.${styles.carouselSlide}`,
            onResize: (width) => {
                if (!carouselRef.current) return;

                carouselRef.current.style.setProperty('--carousel-scroller-width', `${width}px`);
            },
            initialIndex,
        });

        const { scrollToPrevious, scrollToNext, scrollToIndex, refresh } = useMemo(
            () => ({
                scrollToPrevious: () => {
                    if (scrollerRef.current) {
                        scroller.scrollTo('start', calculateScrollDistance(scrollerRef.current));
                    }
                },
                scrollToNext: () => {
                    if (scrollerRef.current) {
                        scroller.scrollTo('end', calculateScrollDistance(scrollerRef.current));
                    }
                },
                scrollToIndex: (index: number, options?: ScrollIntoViewOptions) => {
                    scroller.scrollToIndex(index, options);
                },
                refresh: scroller.refresh,
            }),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [scroller]
        );

        useImperativeHandle(
            ref,
            () => ({
                scrollerRef,
                refresh,
                scrollToIndex,
                scrollToPrevious,
                scrollToNext,
            }),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [scrollToIndex, scrollToNext, scrollToPrevious]
        );

        const handleMouseEnter = useCallback(() => {
            carouselRef.current?.classList.add(styles.carouselHovering);
        }, []);

        const handleMouseLeave = useCallback(() => {
            carouselRef.current?.classList.remove(styles.carouselHovering);
        }, []);

        const isRenderProp = typeof children === 'function';

        const childrenContent = isRenderProp
            ? children({
                  carouselRef,
                  scrollerRef,
                  scrollToPrevious,
                  scrollToNext,
              })
            : children;

        const SlideWrapperComponent = fadeOnVisibility
            ? WithVisibilityFadeSlideWrapper
            : SlideWrapper;

        return (
            <div
                id={id}
                ref={carouselRef}
                className={classnames(styles.carousel, className)}
                style={style}
            >
                <div ref={scrollerRef} className={styles.carouselScroller}>
                    <ul
                        className={classnames(styles.scrollerContent, scrollerContentClassName)}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        {Children.map(childrenContent, (child) => {
                            if (!child) return null;

                            if (isRenderProp) return child;

                            return (
                                <SlideWrapperComponent className={slideClassName}>
                                    {child}
                                </SlideWrapperComponent>
                            );
                        })}
                    </ul>
                </div>

                <CarouselNav
                    className={classnames(
                        styles.carouselNav,
                        styles.carouselNavPrev,
                        navButtonsClassName
                    )}
                    onClick={scrollToPrevious}
                    signal={scroller.startReached}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                />
                <CarouselNav
                    className={classnames(
                        styles.carouselNav,
                        styles.carouselNavNext,
                        navButtonsClassName
                    )}
                    onClick={scrollToNext}
                    signal={scroller.endReached}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                />
            </div>
        );
    }
);
